import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Menu, Kanban } from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import { Sheet, SheetContent, SheetTrigger } from '@/app/components/ui/sheet';
import { Button } from '@/app/components/ui/button';
import { ModuleSidebar } from '@/app/components/navigation/module-sidebar';
import { MODULE_NAVIGATION_ITEMS } from '@/app/components/navigation/primary-navigation';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { useIsTablet } from '@/hooks/use-touch-device';

interface BottomNavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  moduleKey?: string;
}

const MAX_MODULE_SLOTS_PHONE = 3;
const MAX_MODULE_SLOTS_TABLET = 5;

const MODULE_NAV_PRIORITY: BottomNavItem[] = MODULE_NAVIGATION_ITEMS.map(
  ({ label, ...item }) => ({ ...item, name: label }),
);

export function MobileNav() {
  const location = useLocation();
  const { isModuleEnabled } = usePlatformModules();
  const permissions = usePermissions();
  const { isAdmin, hasModuleAccess } = permissions;
  const [moreOpen, setMoreOpen] = useState(false);

  const isTablet = useIsTablet();

  const bottomNav = useMemo(() => {
    const enabledModules = MODULE_NAV_PRIORITY.filter(
      (item) =>
        item.moduleKey &&
        isModuleEnabled(item.moduleKey) &&
        hasModuleAccess(item.moduleKey),
    );
    const maxSlots = isTablet ? MAX_MODULE_SLOTS_TABLET : MAX_MODULE_SLOTS_PHONE;
    const moduleSlots = enabledModules.slice(0, maxSlots);

    // Keep the module the user is currently in reachable from the bottom bar
    // instead of buried under "More". If it overflowed the visible slots,
    // swap it into the last slot.
    const activeModule = enabledModules.find((item) =>
      location.pathname.startsWith(item.href),
    );
    if (
      activeModule &&
      !moduleSlots.some((item) => item.moduleKey === activeModule.moduleKey) &&
      moduleSlots.length > 0
    ) {
      moduleSlots[moduleSlots.length - 1] = activeModule;
    }

    return [
      { name: 'Board', href: '/h3ightboard', icon: Kanban } as BottomNavItem,
      ...moduleSlots,
      // Every role keeps the Admin group (People, Documents, Alerts...);
      // only org Admins enter it at the org-wide Admin Dashboard.
      {
        name: 'Admin',
        href: isAdmin ? '/dashboard' : '/people',
        icon: Shield,
      } as BottomNavItem,
    ];
  }, [hasModuleAccess, isAdmin, isModuleEnabled, isTablet, location.pathname]);

  const gridCols = bottomNav.length + 1; // +1 for the "More" button

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-card border-t border-gray-200 dark:border-border print:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <nav className="grid h-14 sm:h-16" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
        {bottomNav.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex flex-col items-center justify-center gap-1',
                isActive ? 'text-blue-700 dark:text-blue-400' : 'text-gray-600 dark:text-muted-foreground'
              )}
            >
              <item.icon className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="text-xs sm:text-sm font-medium">{item.name}</span>
            </Link>
          );
        })}
        
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              className="flex flex-col items-center justify-center gap-1 h-full rounded-none text-gray-600 dark:text-muted-foreground"
            >
              <Menu className="h-5 w-5" />
              <span className="text-xs font-medium">More</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="p-0 w-64"
            onClickCapture={(event) => {
              if (
                event.target instanceof Element &&
                event.target.closest('a')
              ) {
                setMoreOpen(false);
              }
            }}
          >
            <ModuleSidebar mobile />
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
